// src/admin/screens/loans/actions/CancelLoanModal.tsx
// Cancels a loan (only valid before disbursement). A reason is required so
// that a future audit can reconstruct why. Reopening a cancelled loan goes
// through admin_action_requests — cancellation itself is a direct admin write.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { LoanDetail } from '@admin/hooks/useLoan';

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function CancelLoanModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast({ kind: 'error', msg: 'Reason is required (at least 3 chars).' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase
        .from('loans')
        .update({
          status: 'cancelled',
          cancellation_reason: reason.trim(),
          cancelled_at: new Date().toISOString(),
        })
        .eq('id', loan.id);
      if (error) throw error;
      await recordAudit({
        action: 'loan.cancel',
        target_type: 'loan',
        target_id: loan.id,
        payload: { reason: reason.trim() },
      });
      toast({ kind: 'success', msg: `${loan.loan_number} cancelled` });
      await qc.invalidateQueries({ queryKey: ['loan', loan.id] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title={`Cancel ${loan.loan_number}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="small">
          Cancellation is permanent. Only use before disbursement. To reopen a
          cancelled loan, a super admin must approve a request.
        </p>
        <FormField label="Reason" htmlFor="cancel-reason" required>
          <textarea
            id="cancel-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Back
          </Button>
          <Button variant="danger" onClick={submit} loading={busy}>
            Cancel loan
          </Button>
        </div>
      </div>
    </Modal>
  );
}
